import ElectronicAddressBadge from '@/components/invoices/ElectronicAddressBadge'
import MonthNavigation from '@/components/recaps/MonthNavigation'
import { InvoiceBatchCandidate, InvoiceBatchEarlierMonth, SharedProps } from '@/types'
import { formatCurrency } from '@/utils/format'
import { Head, Link, router, usePage } from '@inertiajs/react'
import { Alert, Anchor, Button, Checkbox, Group, List, Paper, Stack, Table, Text, Title, UnstyledButton } from '@mantine/core'
import { IconAlertTriangle, IconChevronLeft, IconSend } from '@tabler/icons-react'
import dayjs from 'dayjs'
import { useState } from 'react'

function CandidateRow({
  candidate,
  month,
  checked,
  onToggle,
}: {
  candidate: InvoiceBatchCandidate
  month: string
  checked: boolean
  onToggle: (contractId: number) => void
}) {
  const { current_organization } = usePage<SharedProps>().props
  const basePath = `/organizations/${current_organization?.slug}`
  const { contract } = candidate

  const handleToggle = () => onToggle(contract.id)

  return (
    <Table.Tr c={candidate.blocker ? 'dimmed' : undefined}>
      <Table.Td>
        <Checkbox
          checked={checked}
          disabled={!!candidate.blocker}
          onChange={handleToggle}
          aria-label={`Facturer ${contract.customer_name}`}
        />
      </Table.Td>
      <Table.Td>
        <Stack gap={2}>
          <Anchor component={Link} href={`${basePath}/invoices/new?contract_id=${contract.id}&month=${month}`} fw={600} size="sm">
            {contract.customer_name}
          </Anchor>
          {candidate.blocker && (
            <Text size="xs" c="red.8">
              {candidate.blocker}
            </Text>
          )}
        </Stack>
      </Table.Td>
      <Table.Td>
        {contract.electronic_address ? <ElectronicAddressBadge status={contract.electronic_address_status} /> : '—'}
      </Table.Td>
      <Table.Td ta="right">{candidate.orders_count}</Table.Td>
      <Table.Td ta="right">{candidate.services_count}</Table.Td>
      <Table.Td ta="right">{formatCurrency(candidate.total_excluding_tax)}</Table.Td>
    </Table.Tr>
  )
}

function EarlierMonthsAlert({ earlierMonths }: { earlierMonths: InvoiceBatchEarlierMonth[] }) {
  const { current_organization } = usePage<SharedProps>().props
  const basePath = `/organizations/${current_organization?.slug}`

  return (
    <Alert variant="light" color="orange" icon={<IconAlertTriangle />} title="Dépôts non facturés des mois précédents">
      <Text size="sm" mb="xs">
        Ces dépôts ont été confirmés ou ajoutés après la facturation de leur mois. Facturez-les dans leur mois, ou
        marquez-les « Ne pas facturer » depuis la société.
      </Text>
      <List size="sm" spacing={4}>
        {earlierMonths.map((earlier) => (
          <List.Item key={earlier.month}>
            <Anchor component={Link} href={`${basePath}/invoice_batches/new?month=${earlier.month}`} size="sm" fw={600}>
              {dayjs(`${earlier.month}-01`).format('MMMM YYYY')}
            </Anchor>
            {` — ${earlier.services_count} prestation${earlier.services_count > 1 ? 's' : ''}, ` +
              `${earlier.contracts_count} société${earlier.contracts_count > 1 ? 's' : ''}, ` +
              `${formatCurrency(earlier.total_excluding_tax)} HT`}
          </List.Item>
        ))}
      </List>
    </Alert>
  )
}

export default function InvoiceBatchesNew({
  month,
  candidates,
  earlier_months,
}: {
  month: string
  candidates: InvoiceBatchCandidate[]
  earlier_months: InvoiceBatchEarlierMonth[]
}) {
  const { current_organization } = usePage<SharedProps>().props
  const basePath = `/organizations/${current_organization?.slug}`
  const readyIds = candidates.filter((candidate) => !candidate.blocker).map((candidate) => candidate.contract.id)
  const [selectedIds, setSelectedIds] = useState<number[]>(readyIds)
  const [submitting, setSubmitting] = useState(false)

  const selectedTotal = candidates
    .filter((candidate) => selectedIds.includes(candidate.contract.id))
    .reduce((sum, candidate) => sum + Number(candidate.total_excluding_tax), 0)
  const allSelected = readyIds.length > 0 && readyIds.every((id) => selectedIds.includes(id))

  const handleBack = () => router.get(`${basePath}/invoices`)

  const handleMonthChange = (value: string) => router.get(`${basePath}/invoice_batches/new`, { month: value })

  const handleToggle = (contractId: number) =>
    setSelectedIds((current) =>
      current.includes(contractId) ? current.filter((id) => id !== contractId) : [...current, contractId],
    )

  const handleToggleAll = () => setSelectedIds(allSelected ? [] : readyIds)

  const handleFinish = () => setSubmitting(false)

  const handleSubmit = () => {
    const count = selectedIds.length
    const message =
      `Émettre ${count} facture${count > 1 ? 's' : ''} pour ${formatCurrency(selectedTotal)} HT ? ` +
      'Chacune sera transmise à la plateforme de réception du client et ne pourra plus être supprimée.'
    if (!confirm(message)) return
    setSubmitting(true)
    // Each société's invoice bills the services shown here, not a deposit confirmed since the page was loaded.
    const selection = Object.fromEntries(
      candidates
        .filter((candidate) => selectedIds.includes(candidate.contract.id))
        .map((candidate) => [candidate.contract.id, candidate.service_ids]),
    )
    router.post(`${basePath}/invoice_batches`, { month, selection }, { onFinish: handleFinish })
  }

  return (
    <>
      <Head title={`Facturer le mois - ${current_organization?.name}`} />

      <Stack gap="md">
        <UnstyledButton onClick={handleBack} w="fit-content">
          <Group gap={4} c="dimmed">
            <IconChevronLeft size={18} />
            <Text size="sm">Factures</Text>
          </Group>
        </UnstyledButton>

        <Title order={3}>{`Facturer le mois — ${dayjs(`${month}-01`).format('MMMM YYYY')}`}</Title>
        <Text c="dimmed" size="sm">
          {current_organization?.invoicing_basis === 'completion'
            ? "Chaque société reçoit une facture avec toutes ses prestations réalisées dans le mois et pas encore facturées ; celles en cours attendent le mois où l'atelier les termine."
            : 'Chaque société reçoit une facture avec tous ses dépôts non facturés du mois.'}{' '}
          Pour en exclure un, ouvrez la société.
        </Text>

        <MonthNavigation month={month} onChange={handleMonthChange} />

        {earlier_months.length > 0 && <EarlierMonthsAlert earlierMonths={earlier_months} />}

        <Paper withBorder radius="md" p={{ base: 0, sm: 'md' }}>
          <Table.ScrollContainer minWidth={700}>
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={40}>
                    <Checkbox
                      checked={allSelected}
                      indeterminate={selectedIds.length > 0 && !allSelected}
                      disabled={readyIds.length === 0}
                      onChange={handleToggleAll}
                      aria-label="Tout sélectionner"
                    />
                  </Table.Th>
                  <Table.Th>Société</Table.Th>
                  <Table.Th>Adresse électronique</Table.Th>
                  <Table.Th ta="right">Tickets</Table.Th>
                  <Table.Th ta="right">Prestations</Table.Th>
                  <Table.Th ta="right">Total HT</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {candidates.length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={6}>
                      <Text c="dimmed" ta="center" py="md">
                        Tous les dépôts de ce mois sont déjà facturés
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
                {candidates.map((candidate) => (
                  <CandidateRow
                    key={candidate.contract.id}
                    candidate={candidate}
                    month={month}
                    checked={selectedIds.includes(candidate.contract.id)}
                    onToggle={handleToggle}
                  />
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Paper>

        <Group justify="flex-end">
          <Button
            size="md"
            leftSection={<IconSend size={18} />}
            onClick={handleSubmit}
            loading={submitting}
            disabled={selectedIds.length === 0}
          >
            {`Créer et envoyer ${selectedIds.length} facture${selectedIds.length > 1 ? 's' : ''}`}
          </Button>
        </Group>
      </Stack>
    </>
  )
}
